import { ImpactActions } from '../actions/impacts';
import { ImpactConfigActions } from '../actions/config';
import { ClickTypeFilter, ImpactTypeFilter, ImpactTypes } from '../types';
import { combineReducers } from '@fullstory/redux-util/utils/combineReducers';
import {
  BadClickType,
  CustomEvent,
  ImpactType,
  PageView,
  SeenEvent,
} from '@fullstory/proto/fs/services/alexa';
import { CustomQueryProto } from '@fullstory/proto/fs/services/webber/internal/impacts';
import { DateRangeID } from '@fullstory/client-api';
import { resetDateToMidnight } from '@fullstory/util/date';
import { defaultClickFilter, defaultImpactFilter } from './currentConfig';
import { Timestamp } from '@fullstory/proto/google/protobuf';
import { adjustSinceDateIfNeeded } from '../util';

type PendingConfigState = {
  funnelId: ReturnType<typeof pendingFunnelId>;
  segmentId: ReturnType<typeof pendingSegmentId>;
  cannedDateRangeId: ReturnType<typeof pendingCannedDateRangeId>;
  startDate: ReturnType<typeof pendingStartDate>;
  endDate: ReturnType<typeof pendingEndDate>;
  customEvents: ReturnType<typeof pendingCustomEvents>;
  watchedElements: ReturnType<typeof pendingWatchedElements>;
  customQueries: ReturnType<typeof pendingCustomQueries>;
  clickTypeFilter: ReturnType<typeof pendingClickTypeFilter>;
  impactTypeFilter: ReturnType<typeof pendingImpactTypeFilter>;
  definedEventIds: ReturnType<typeof pendingDefinedEventIds>;
  unnamed: ReturnType<typeof pendingFunnelIsUnnamed>;
  pageviewEvents: ReturnType<typeof pendingPageviewEvents>;
};

export const pendingConfig = combineReducers<PendingConfigState>({
  funnelId: pendingFunnelId,
  segmentId: pendingSegmentId,
  cannedDateRangeId: pendingCannedDateRangeId,
  startDate: pendingStartDate,
  endDate: pendingEndDate,
  customEvents: pendingCustomEvents,
  watchedElements: pendingWatchedElements,
  customQueries: pendingCustomQueries,
  clickTypeFilter: pendingClickTypeFilter,
  impactTypeFilter: pendingImpactTypeFilter,
  definedEventIds: pendingDefinedEventIds,
  unnamed: pendingFunnelIsUnnamed,
  pageviewEvents: pendingPageviewEvents,
});

function pendingFunnelId(
  state: string | null = null,
  action: ImpactActions | ImpactConfigActions,
): string | null {
  switch (action.type) {
    case ImpactTypes.ChangePendingFunnel:
      return action.funnelId;
    case ImpactTypes.ConfigLoad:
      return action.config.funnelId;
    case ImpactTypes.ConfigNew:
      return null;
  }
  return state;
}

function pendingFunnelIsUnnamed(
  state: boolean = false,
  action: ImpactActions | ImpactConfigActions,
): boolean {
  switch (action.type) {
    case ImpactTypes.ChangePendingFunnel:
      return action.unnamed ?? false;
    case ImpactTypes.ConfigLoad:
      return action.config.unnamed ?? false;
    case ImpactTypes.ConfigNew:
      return false;
  }
  return state;
}

function pendingSegmentId(
  state: string = 'everyone',
  action: ImpactActions | ImpactConfigActions,
): string {
  switch (action.type) {
    case ImpactTypes.ChangePendingSegment:
      return action.segmentId;
    case ImpactTypes.ConfigLoad:
      return action.config.segmentId;
    case ImpactTypes.ConfigNew:
      return 'everyone';
  }
  return state;
}

function pendingCannedDateRangeId(
  state: DateRangeID = DateRangeID.PastWeek,
  action: ImpactActions | ImpactConfigActions,
): DateRangeID {
  switch (action.type) {
    case ImpactTypes.SetPendingCannedDateRangeID:
      return action.id;
    case ImpactTypes.ConfigLoad: {
      return action.config.dateRangeId as DateRangeID;
    }
    case ImpactTypes.ConfigNew:
      return DateRangeID.PastWeek;
  }
  return state;
}

/** Overridden date range with explicit start date */
function pendingStartDate(
  state: Date | null = null,
  action: ImpactActions | ImpactConfigActions,
): Date | null {
  switch (action.type) {
    case ImpactTypes.SetPendingStartDate:
      return action.date;
    case ImpactTypes.ConfigNew: {
      return null;
    }
    case ImpactTypes.SetPendingCannedDateRangeID:
      // When setting the canned date range to Custom, set startDate to Today as a default value
      return action.id === DateRangeID.Custom ? resetDateToMidnight(new Date()) : null;
    case ImpactTypes.ConfigLoad:
      if (action.config.dateRangeId === DateRangeID.Since) {
        return adjustSinceDateIfNeeded(Timestamp.toDate(action.config.startTime));
      } else {
        return Timestamp.toDate(action.config.startTime);
      }
  }
  return state;
}

/** Overridden date range with explicit end date */
function pendingEndDate(
  state: Date | null = null,
  action: ImpactActions | ImpactConfigActions,
): Date | null {
  switch (action.type) {
    case ImpactTypes.SetPendingEndDate:
      return action.date;
    case ImpactTypes.ConfigNew:
      return null;
    case ImpactTypes.SetPendingCannedDateRangeID:
      // When setting the canned date range to Custom, set endDate to Today as a default value
      return action.id === DateRangeID.Custom ? resetDateToMidnight(new Date()) : null;
    case ImpactTypes.ConfigLoad:
      return Timestamp.toDate(action.config.endTime);
  }
  return state;
}

function pendingCustomEvents(
  state: CustomEvent[] = [],
  action: ImpactActions | ImpactConfigActions,
): CustomEvent[] {
  switch (action.type) {
    case ImpactTypes.ConfigLoad:
      return action.config.customEvents ? action.config.customEvents : [];
    case ImpactTypes.ChangePendingInputs: {
      const customEvent = action.inputs.currentCustomEvents;
      if (!customEvent) {
        throw Error(`action.inputs.currentCustomEvents is ${customEvent}`);
      }
      return customEvent;
    }
    case ImpactTypes.ConfigNew:
      return action.defaultSpecSignals?.customEvents ?? [];
  }
  return state;
}

function pendingPageviewEvents(
  state: PageView[] = [],
  action: ImpactActions | ImpactConfigActions,
): PageView[] {
  switch (action.type) {
    case ImpactTypes.ConfigLoad:
      return action.config.pageviewEvents ? action.config.pageviewEvents : [];
    case ImpactTypes.ChangePendingInputs: {
      const pageviewEvents = action.inputs.currentPageviewEvents;
      if (!pageviewEvents) {
        return [];
      }
      return pageviewEvents;
    }
    case ImpactTypes.ConfigNew:
      return action.defaultSpecSignals?.pageviewEvents ?? [];
  }
  return state;
}

function pendingWatchedElements(
  state: SeenEvent[] = [],
  action: ImpactActions | ImpactConfigActions,
): SeenEvent[] {
  switch (action.type) {
    case ImpactTypes.ConfigLoad:
      return action.config.watchedElements ? action.config.watchedElements : [];
    case ImpactTypes.ChangePendingInputs: {
      const watchedElements = action.inputs.currentWatchedElements;
      if (!watchedElements) {
        throw Error(`action.inputs.currentWatchedElement is ${watchedElements}`);
      }
      return watchedElements;
    }
    case ImpactTypes.ConfigNew:
      return action.defaultSpecSignals?.seenEvents ?? [];
  }
  return state;
}

function pendingCustomQueries(
  state: CustomQueryProto[] = [],
  action: ImpactActions | ImpactConfigActions,
): CustomQueryProto[] {
  switch (action.type) {
    case ImpactTypes.ConfigLoad:
      return action.config.customQueries ? action.config.customQueries : [];
    case ImpactTypes.ChangePendingInputs: {
      const customQueries = action.inputs.currentCustomQueries;
      if (!customQueries) {
        throw Error(`action.inputs.currentCustomQueries is ${customQueries}`);
      }
      return customQueries;
    }
    case ImpactTypes.ConfigNew:
      return [];
  }
  return state;
}

function pendingClickTypeFilter(
  state: ClickTypeFilter = defaultClickFilter,
  action: ImpactActions | ImpactConfigActions,
): ClickTypeFilter {
  switch (action.type) {
    case ImpactTypes.ConfigLoad: {
      const loaded: { [key: string]: boolean | undefined } = { ...defaultClickFilter };
      Object.keys(loaded).forEach(
        key => (loaded[key] = action.config.clickTypeFilters.includes(+key)),
      );
      return loaded;
    }
    case ImpactTypes.ChangePendingInputs:
      return {
        ...state,
        [BadClickType.CLICK_TYPE_RAGE]: action.inputs.filterRageClicks,
        [BadClickType.CLICK_TYPE_ERROR]: action.inputs.filterErrorClicks,
        [BadClickType.CLICK_TYPE_DEAD]: action.inputs.filterDeadClicks,
      };
    case ImpactTypes.FilterRageClicks:
      return {
        ...state,
        [BadClickType.CLICK_TYPE_RAGE]: action.filtered,
      };
    case ImpactTypes.FilterErrorClicks:
      return {
        ...state,
        [BadClickType.CLICK_TYPE_ERROR]: action.filtered,
      };
    case ImpactTypes.FilterDeadClicks:
      return {
        ...state,
        [BadClickType.CLICK_TYPE_DEAD]: action.filtered,
      };
    case ImpactTypes.ConfigNew:
      return defaultClickFilter;
  }
  return state;
}

/** The set of impact type filters that will be applied to `impacts` */
function pendingImpactTypeFilter(
  state: ImpactTypeFilter = defaultImpactFilter,
  action: ImpactActions | ImpactConfigActions,
): ImpactTypeFilter {
  switch (action.type) {
    case ImpactTypes.ConfigLoad: {
      const loaded: { [key: string]: boolean | undefined } = { ...defaultImpactFilter };
      Object.keys(loaded).forEach(
        key => (loaded[key] = action.config.impactTypeFilters.includes(+key)),
      );
      // if legacy web vital option is set, we need to set the new web vitals to the same value
      if (loaded[ImpactType.IMPACT_TYPE_CORE_WEB_VITALS] && action.splitWebVitals) {
        loaded[ImpactType.IMPACT_TYPE_FID] = true;
        loaded[ImpactType.IMPACT_TYPE_LCP] = true;
        loaded[ImpactType.IMPACT_TYPE_CLS] = true;
        loaded[ImpactType.IMPACT_TYPE_TBT] = true;
        loaded[ImpactType.IMPACT_TYPE_INP] = true;
        loaded[ImpactType.IMPACT_TYPE_TTI] = true;
        loaded[ImpactType.IMPACT_TYPE_TTFB] = true;
      }
      return loaded;
    }
    case ImpactTypes.ChangePendingInputs:
      return {
        ...state,
        [ImpactType.IMPACT_TYPE_PERF_EVENT]: action.inputs.filterPagePerformance,
        [ImpactType.IMPACT_TYPE_AJAX_ERROR]: action.inputs.filterAjaxErrors,
        [ImpactType.IMPACT_TYPE_CORE_WEB_VITALS]: action.inputs.filterCoreWebVitals,
        [ImpactType.IMPACT_TYPE_FID]: action.inputs.filterFid,
        [ImpactType.IMPACT_TYPE_LCP]: action.inputs.filterLcp,
        [ImpactType.IMPACT_TYPE_CLS]: action.inputs.filterCls,
        [ImpactType.IMPACT_TYPE_TBT]: action.inputs.filterTbt,
        [ImpactType.IMPACT_TYPE_INP]: action.inputs.filterInp,
        [ImpactType.IMPACT_TYPE_TTI]: action.inputs.filterTti,
        [ImpactType.IMPACT_TYPE_TTFB]: action.inputs.filterTtfb,
      };
    case ImpactTypes.FilterPagePerformance:
      return {
        ...state,
        [ImpactType.IMPACT_TYPE_PERF_EVENT]: action.filtered,
      };
    case ImpactTypes.FilterAjaxErrors:
      return {
        ...state,
        [ImpactType.IMPACT_TYPE_AJAX_ERROR]: action.filtered,
      };
    case ImpactTypes.ConfigNew:
      return {
        ...defaultImpactFilter,
        [ImpactType.IMPACT_TYPE_CORE_WEB_VITALS]: !action.splitWebVitals,
        [ImpactType.IMPACT_TYPE_FID]: action.splitWebVitals,
        [ImpactType.IMPACT_TYPE_LCP]: action.splitWebVitals,
        [ImpactType.IMPACT_TYPE_CLS]: action.splitWebVitals,
        [ImpactType.IMPACT_TYPE_TTFB]: action.splitWebVitals,
        [ImpactType.IMPACT_TYPE_INP]: action.splitWebVitals,
        [ImpactType.IMPACT_TYPE_TBT]: false, // always default to false
        [ImpactType.IMPACT_TYPE_TTI]: false, // always default to false
      };
  }
  return state;
}

function pendingDefinedEventIds(
  state: string[] = [],
  action: ImpactActions | ImpactConfigActions,
): string[] {
  switch (action.type) {
    case ImpactTypes.ConfigLoad:
      return action.config.definedEventIds ? action.config.definedEventIds : [];
    case ImpactTypes.ChangePendingInputs: {
      const definedEvents = action.inputs.currentDefinedEvents;
      if (!definedEvents) {
        throw Error(`action.inputs.currentDefinedEvents is ${definedEvents}`);
      }
      return definedEvents?.map(d => d.id);
    }
    case ImpactTypes.ConfigNew:
      return [];
  }
  return state;
}
